import { CDN } from '../lib/cdn';

export { SOLUTIONS_BREADCRUMB } from './forFilmmakersPage';

export const AGENCIES_FAQ = [
  {
    q: 'Can we keep brand and character consistent across deliverables?',
    a: 'Yes. Each client gets a continuity graph holding their characters, products, locations, and look across every scene and project — so the brand stays right in shot 1 and shot 40, with no editor chasing drift.',
  },
  {
    q: 'How fast can we turn around revisions?',
    a: 'Client notes apply to single shots without re-rolling the cut, so revisions take hours, not another production cycle. First cut is same-day; changes are shot-level edits, not reshoots.',
  },
  {
    q: 'Do we get commercial rights?',
    a: 'Yes — full commercial licensing on every tier and project. Client content stays yours with a training opt-out: no per-deliverable licensing, no hidden carve-outs.',
  },
] as const;

export type AgencyCellTone = 'dg' | 'do' | 'plain';

export type AgencyCompareCell = { tone: AgencyCellTone; label: string };

export const AGENCIES_COMPARE_ROWS: {
  capability: string;
  induce: AgencyCompareCell;
  runway: AgencyCompareCell;
  heygen: AgencyCompareCell;
  stack: AgencyCompareCell;
}[] = [
  {
    capability: 'Starting point',
    induce: { tone: 'plain', label: 'Client brief or screenplay' },
    runway: { tone: 'plain', label: 'Single clip prompt' },
    heygen: { tone: 'plain', label: 'Avatar script' },
    stack: { tone: 'plain', label: 'Different per tool' },
  },
  {
    capability: 'Script-to-cut pipeline',
    induce: { tone: 'dg', label: 'Full breakdown' },
    runway: { tone: 'do', label: 'Clip-by-clip only' },
    heygen: { tone: 'do', label: 'No script input' },
    stack: { tone: 'do', label: 'Manual stitching' },
  },
  {
    capability: 'Brand continuity across project',
    induce: { tone: 'dg', label: 'Continuity graph' },
    runway: { tone: 'do', label: 'World Consistency (50+ shots)' },
    heygen: { tone: 'do', label: 'Avatar only' },
    stack: { tone: 'do', label: 'No shared continuity' },
  },
  {
    capability: 'Per-client project isolation',
    induce: { tone: 'dg', label: 'Yes - scoped per project' },
    runway: { tone: 'do', label: 'Workspace folders' },
    heygen: { tone: 'do', label: 'Team seats' },
    stack: { tone: 'do', label: 'Manual across platforms' },
  },
  {
    capability: 'Billing model',
    induce: { tone: 'dg', label: 'Single plan · predictable' },
    runway: { tone: 'do', label: 'Credits expire monthly' },
    heygen: { tone: 'do', label: '$149/mo · per-seat' },
    stack: { tone: 'do', label: '3–5 billing lines' },
  },
  {
    capability: 'Multi-model routing',
    induce: { tone: 'dg', label: 'Kling · Seedance · Veo · Nano Banana' },
    runway: { tone: 'do', label: 'Own models only' },
    heygen: { tone: 'do', label: 'Own engine only' },
    stack: { tone: 'do', label: 'One at a time, manual' },
  },
  {
    capability: 'Commercial rights for clients',
    induce: { tone: 'dg', label: 'Every tier' },
    runway: { tone: 'do', label: 'Paid tiers' },
    heygen: { tone: 'do', label: 'Creator tier +' },
    stack: { tone: 'do', label: 'Varies per platform' },
  },
  {
    capability: 'Best for',
    induce: { tone: 'plain', label: 'Narrative & campaign creative at volume' },
    runway: { tone: 'plain', label: 'Per-clip cinematic control' },
    heygen: { tone: 'plain', label: 'Avatar spokesperson content' },
    stack: { tone: 'plain', label: 'Teams managing complexity' },
  },
];

export const PV2_STAGES = [
  {
    idx: 0,
    kind: 'script_to_cut' as const,
    label: 'STAGE 01',
    pnum: '01 - BRIEF',
    title: 'Client brief ingested',
    body: 'Upload a brief, treatment, or screenplay - FDX, PDF, Fountain, or plain text. The whole project starts here, not one clip at a time.',
    tags: ['FDX', 'PDF', 'Fountain', 'Plain text'],
    video: CDN.videos.agenciesClientIngest,
  },
  {
    idx: 1,
    kind: 'warehouse' as const,
    label: 'STAGE 02',
    pnum: '02 - BREAKDOWN',
    title: 'Scenes, shots & the continuity graph',
    body: 'Every scene breaks into shots automatically. Characters, products, and locations are cast into the client\'s continuity graph, and the story is logic-checked before a frame renders.',
    tags: ['Slug lines', 'Action cues', 'Dialogue', 'Continuity graph'],
    video: CDN.videos.sceneBreakdown,
  },
  {
    idx: 2,
    kind: 'camera_abstract' as const,
    label: 'STAGE 03',
    pnum: '03 - GENERATION',
    title: 'The right engine, every shot',
    body: 'Each shot routes to the engine built for it - dialogue to Kling, narrative to Seedance, hero shots to Veo, stylized work to Nano Banana - while a virtual DOP frames the coverage.',
    tags: ['Kling', 'Seedance', 'Veo', 'Nano Banana'],
    video: CDN.videos.agenciesStage03,
  },
  {
    idx: 3,
    kind: 'dawn' as const,
    label: 'STAGE 04',
    pnum: '04 - REVIEW',
    title: 'Notes, not re-rolls',
    body: 'Notes on any shot - angle, light, pacing - land locally. Client revisions turn around in hours, and approved shots never move.',
    tags: ['Local override', 'Client revisions', 'Approved shots'],
    video: CDN.videos.agenciesStage04,
  },
  {
    idx: 4,
    kind: 'final_cut' as const,
    label: 'STAGE 05',
    pnum: '05 - DELIVERY',
    title: 'Every format from one cut',
    body: 'Broadcast :30, social 9:16, pre-roll :15 - exported as MP4, ProRes, or EDL, or handed straight to your editor.',
    tags: ['MP4', 'ProRes', 'EDL', 'Commercial rights'],
    video: CDN.videos.filmCut,
  },
];

export const ECON_BEFORE = {
  etag: 'Traditional production',
  titleEm: 'Unpredictable cost.',
  title: 'Weeks of production. Dozens of vendor conversations.',
  rows: [
    { label: 'Cost per finished minute', value: '$5,000 – $15,000' },
    { label: 'Turnaround time per project', value: '4 – 8 weeks' },
    { label: 'Brand consistency across cuts', value: 'Manual - editor-dependent' },
    { label: 'Multi-client tool management', value: '5+ subscriptions, billing lines' },
    { label: 'Model/vendor dependency', value: 'Single vendor lock-in' },
    { label: 'Commercial rights', value: 'Licensed per deliverable, per contract' },
  ],
};

export const ECON_AFTER = {
  etag: 'With Induce',
  title: 'Hours to a first cut. One pipeline. Predictable output.',
  rows: [
    { label: 'Cost per finished minute', value: '~$300 – $600' },
    { label: 'Turnaround time per project', value: 'Hours to days' },
    { label: 'Brand consistency across cuts', value: 'Continuity graph - automatic' },
    { label: 'Multi-client tool management', value: '1 platform · 1 billing line' },
    { label: 'Model/vendor dependency', value: 'Model-agnostic orchestration' },
    { label: 'Commercial rights', value: 'Full rights - every tier, every project' },
  ],
};

export const SPEC_WORKFLOW = {
  title: 'Workflow & Output',
  badge: 'Generally available',
  rows: [
    { key: 'Script formats', value: 'FDX · PDF · Fountain · plain text' },
    { key: 'Export formats', value: 'MP4 · ProRes · EDL' },
    { key: 'Models orchestrated', value: 'Kling · Seedance · Veo · Nano Banana' },
    { key: 'Continuity layer', value: 'Persistent per-project memory graph' },
    { key: 'Shot overrides', value: 'Local - no full re-roll' },
    { key: 'Per-client isolation', value: 'Scoped per project' },
    { key: 'Concurrent projects', value: 'Unlimited' },
    { key: 'Team collaboration', value: 'Coming soon', highlight: 'api' as const },
  ],
};

export const SPEC_AGENCY = {
  title: 'Agency & client controls',
  badge: 'Every tier',
  rows: [
    { key: 'Per-client isolation', value: 'Scoped per project' },
    { key: 'Concurrent projects', value: 'Unlimited' },
    { key: 'Commercial rights', value: 'Every tier, every project', yes: true },
    { key: 'Training opt-out', value: 'Client content stays yours' },
    { key: 'Shot override', value: 'Local - no full re-roll' },
    { key: 'Export formats', value: 'MP4 · ProRes · EDL' },
    { key: 'Team access', value: 'Coming soon', highlight: 'api' as const },
    { key: 'Volume pricing', value: 'Available on request' },
  ],
};
